/**
 * Session Launcher Engine
 *
 * Pure business-rules function — no UI, no Supabase calls.
 *
 * Given a LaunchContext (which tool, what exists in the workspace), it returns
 * a LaunchPlan that tells the launcher UI:
 *  - what session context mode to use
 *  - which S-class to propose
 *  - what the variant fit is
 *  - what was inherited automatically vs what still needs to be asked
 *  - which prerequisites are met, blocking, or just warnings
 *  - what output forms this combination will produce
 *  - where results should be persisted
 */

import type { DecisionClass } from '../types/prioritization/session';
import type { SessionContextMode } from '../types/prioritization/session';
import {
  type MethodVariant,
  type SessionFit,
  type OutputForm,
  type MethodPrerequisiteCode,
  getVariantsForTool,
  getVariantById,
  getDefaultModalityForTool,
} from './methodRegistry';

// ============================================================================
// Input: Launch Context
// ============================================================================

export interface LaunchContext {
  /** Tool slug, e.g. 'eisenhower-matrix' */
  toolId: string;
  /** Pre-selected variant (e.g. from tool selector recommendation) */
  variantId?: string;
  /** If present → cycle_linked candidate */
  cycleId?: string;
  /**
   * If present → inherits item pool from track.
   * For standalone tracks (no cycleId), this is still set — the session is
   * linked to the track container even without a parent cycle.
   */
  trackId?: string;
  /** True when the track is a standalone track (cycleId is null/absent). */
  isStandaloneTrack?: boolean;
  /** Number of workspace items shaped and eligible for tools */
  workspaceItemCount: number;
  /** Whether the cycle/track's pool is already locked */
  poolLocked?: boolean;
  /** Whether criteria set is locked for this track/cycle */
  criteriaLocked?: boolean;
  /** Whether participation has been locked for this cycle/track */
  participationLocked?: boolean;
  /** Whether value assignments are sufficiently complete */
  valuesComplete?: boolean;
  /**
   * For session-first creation (Path C): the meta-category the user selected
   * as the subject of this session ("What do you want to prioritize?").
   * Drives item filtering in the launcher UI.
   */
  selectedMetaCategory?: string;
  /**
   * For session-first creation: number of workspace items matching the
   * selected meta-category (subset of workspaceItemCount).
   */
  metaCategoryItemCount?: number;
}

// ============================================================================
// Output: Launch Plan
// ============================================================================

export type LaunchQuestion =
  | { id: 'session_name'; required: true }
  | { id: 'modality'; options: Array<'Qualitative' | 'Quantitative'>; required: true }
  | { id: 's_class'; options: DecisionClass[]; fitMap: Record<DecisionClass, SessionFit>; required: true }
  | { id: 'item_source'; options: ItemSource[]; required: true }
  | { id: 'result_destination'; options: ResultDestination[]; required: false };

export type ItemSource = 'workspace_items' | 'track_pool' | 'manual';

export type ResultDestination =
  | 'session_results'
  | 'item_pool_update'
  | 'criteria_artifact'
  | 'narrative_artifact';

export interface InheritedFields {
  cycleId?: string;
  trackId?: string;
  isStandaloneTrack?: boolean;
  itemPoolSource?: ItemSource;
  sessionContextMode?: SessionContextMode;
  sClass?: DecisionClass;
  modality?: 'Qualitative' | 'Quantitative';
  /** For session-first (Path C): the meta-category chosen at step 0. */
  selectedMetaCategory?: string;
}

export interface LaunchPlan {
  /** Whether the launch can proceed at all */
  canLaunch: boolean;
  /** Human-readable reason if canLaunch is false */
  blockedReason?: string;

  sessionContextMode: SessionContextMode;
  proposedSClass: DecisionClass;
  variantFit: SessionFit;
  /** The best variant for the proposed context (may differ from requested if not_fit) */
  selectedVariant: MethodVariant | null;

  inheritedContext: InheritedFields;
  questionsRequired: LaunchQuestion[];

  prerequisitesMet: MethodPrerequisiteCode[];
  prerequisitesBlocking: MethodPrerequisiteCode[];
  prerequisitesWarning: MethodPrerequisiteCode[];

  outputForms: OutputForm[];
  resultDestinations: ResultDestination[];
}

// ============================================================================
// Business rules
// ============================================================================

/**
 * Prerequisites that are "warn only" — they don't block launch but the launcher
 * should surface them to the user.
 */
const WARN_ONLY_PREREQS: MethodPrerequisiteCode[] = [
  'LEVELING_PRESENT_WARN_ONLY',
  'POOL_HOMOGENEITY_WARN_ONLY',
];

/**
 * Map a prerequisite code to whether it's satisfied given the launch context.
 * Returns 'met' | 'blocking' | 'warning'.
 */
function evaluatePrerequisite(
  prereq: MethodPrerequisiteCode,
  ctx: LaunchContext,
): 'met' | 'blocking' | 'warning' {
  if (WARN_ONLY_PREREQS.includes(prereq)) return 'warning';

  switch (prereq) {
    case 'METHOD_LOCKED':
      // The method is known (we have a toolId) — always met at launch time
      return 'met';
    case 'PARTICIPATION_LOCKED':
      // Outside of a cycle/track context there is no participation roster to
      // formally lock — treat as automatically met (same pattern as POOL_LOCKED_FOR_S6).
      if (!ctx.trackId && !ctx.cycleId) return 'met';
      return ctx.participationLocked ? 'met' : 'blocking';
    case 'POOL_LOCKED_FOR_S6':
      // Only applicable when launching from a track/cycle context
      if (!ctx.trackId && !ctx.cycleId) return 'met';
      return ctx.poolLocked ? 'met' : 'blocking';
    case 'CRITERIA_SET_LOCKED_FOR_S6':
      if (!ctx.trackId && !ctx.cycleId) return 'met';
      return ctx.criteriaLocked ? 'met' : 'blocking';
    case 'VALUES_COMPLETE_FOR_CRITERIA':
      if (!ctx.trackId && !ctx.cycleId) return 'met';
      return ctx.valuesComplete ? 'met' : 'blocking';
    default:
      return 'met';
  }
}

/**
 * Infer the session context mode from available context.
 *
 * Rules:
 * - trackId present (with or without cycleId) → cycle_linked
 *   Standalone tracks have cycleId === null but still provide a structured
 *   container with S1-S5 config; the session is "linked" to that container.
 * - cycleId only (no track) → cycle_linked
 * - workspace items exist, no cycle/track → app_linked
 * - nothing → isolated
 */
function inferContextMode(ctx: LaunchContext): SessionContextMode {
  if (ctx.trackId || ctx.cycleId) return 'cycle_linked';
  if (ctx.workspaceItemCount > 0) return 'app_linked';
  return 'isolated';
}

/**
 * Infer the default S-class to propose for a given tool and context.
 *
 * Business rules:
 * | trackId present        | Default S6 unless variant is ✅ S2 only        |
 * | cycleId only           | S6 (cycle decision default)                     |
 * | workspace items, no cy | S6 app_linked                                   |
 * | S2-primary tool        | S2 if no pool locked yet                        |
 * | no items at all        | S2 (pool selection first) or S6 with gate        |
 */
function inferProposedSClass(ctx: LaunchContext, variants: MethodVariant[]): DecisionClass {
  // If a specific variant is pre-selected, use its best-fit S-class
  if (ctx.variantId) {
    const v = getVariantById(ctx.variantId);
    if (v) {
      // Find the S-class where this variant is recommended, prefer S6 > S2 > others
      const preferred: DecisionClass[] = [
        'S6_ITEM_PRIORITIZATION',
        'S2_ITEM_POOL_SELECTION',
        'S3_ATTRIBUTE_SELECTION',
        'S1_PARTICIPATION_PRIORITIZATION',
      ];
      for (const sc of preferred) {
        if (v.sessionFit[sc] === 'recommended') return sc;
      }
      for (const sc of preferred) {
        if (v.sessionFit[sc] === 'allowed_warn') return sc;
      }
    }
  }

  // If the tool is primarily S2 (pool selection) and pool isn't locked
  const s2Recommended = variants.some(
    (v) => v.sessionFit.S2_ITEM_POOL_SELECTION === 'recommended',
  );
  const s6Recommended = variants.some(
    (v) => v.sessionFit.S6_ITEM_PRIORITIZATION === 'recommended',
  );

  if (s2Recommended && !s6Recommended && !ctx.poolLocked) {
    return 'S2_ITEM_POOL_SELECTION';
  }

  // Default to S6
  return 'S6_ITEM_PRIORITIZATION';
}

/**
 * Select the best variant for the proposed context.
 * Preference: exact variantId match > recommended for sClass > allowed_warn for sClass.
 */
function selectBestVariant(
  ctx: LaunchContext,
  variants: MethodVariant[],
  proposedSClass: DecisionClass,
): MethodVariant | null {
  if (ctx.variantId) {
    const exact = variants.find((v) => v.variantId === ctx.variantId);
    if (exact) return exact;
  }

  const recommended = variants.find(
    (v) => v.sessionFit[proposedSClass] === 'recommended',
  );
  if (recommended) return recommended;

  const allowed = variants.find(
    (v) => v.sessionFit[proposedSClass] === 'allowed_warn',
  );
  return allowed ?? variants[0] ?? null;
}

/**
 * Determine what questions still need to be asked, given what's inherited.
 */
function buildQuestionsRequired(
  ctx: LaunchContext,
  variants: MethodVariant[],
  proposedSClass: DecisionClass,
  selectedVariant: MethodVariant | null,
  contextMode: SessionContextMode,
): LaunchQuestion[] {
  const questions: LaunchQuestion[] = [];

  // Session name is always required
  questions.push({ id: 'session_name', required: true });

  // Modality: ask only when the tool has both Qual and Quant variants AND no
  // default is declared. When defaultModality is set the question is skipped —
  // the default is pre-selected silently in buildLaunchPlan.
  const hasQual = variants.some((v) => v.modality === 'Qualitative');
  const hasQuant = variants.some((v) => v.modality === 'Quantitative');
  const toolDefaultModality = getDefaultModalityForTool(ctx.toolId);
  if (hasQual && hasQuant && !ctx.variantId && !toolDefaultModality) {
    questions.push({
      id: 'modality',
      options: ['Qualitative', 'Quantitative'],
      required: true,
    });
  }

  // S-class: ask if multiple are available and one wasn't inherited
  const fitMap: Partial<Record<DecisionClass, SessionFit>> = {};
  const allSClasses: DecisionClass[] = [
    'S1_PARTICIPATION_PRIORITIZATION',
    'S2_ITEM_POOL_SELECTION',
    'S3_ATTRIBUTE_SELECTION',
    'S4_VALUES_CHOICE_SELECTION',
    'S5_VALUE_SELECTION',
    'S6_ITEM_PRIORITIZATION',
  ];

  for (const sc of allSClasses) {
    const bestFit = selectedVariant?.sessionFit[sc];
    if (bestFit && bestFit !== 'not_fit') {
      fitMap[sc] = bestFit;
    }
  }

  const eligibleSClasses = Object.keys(fitMap) as DecisionClass[];
  // Ask about S-class only if there are multiple eligible and it's not already inferred
  const sClassAlreadyInherited =
    ctx.trackId || (eligibleSClasses.length === 1);

  if (!sClassAlreadyInherited && eligibleSClasses.length > 1) {
    questions.push({
      id: 's_class',
      options: eligibleSClasses,
      fitMap: fitMap as Record<DecisionClass, SessionFit>,
      required: true,
    });
  }

  // Item source: ask only if not already inherited
  if (!ctx.trackId && !ctx.cycleId && contextMode !== 'isolated') {
    const sources: ItemSource[] = [];
    if (ctx.workspaceItemCount > 0) sources.push('workspace_items');
    sources.push('manual');
    if (sources.length > 1) {
      questions.push({ id: 'item_source', options: sources, required: true });
    }
  }

  // Result destination: ask only when output_form could go multiple places
  const outputForms = selectedVariant?.outputForms ?? [];
  const hasMultipleDestinations =
    outputForms.includes('allocation_distribution') ||
    outputForms.includes('narrative_framing') ||
    (outputForms.includes('tiered_list') && proposedSClass === 'S2_ITEM_POOL_SELECTION');

  if (hasMultipleDestinations) {
    const destinations = deriveResultDestinations(outputForms, proposedSClass);
    if (destinations.length > 1) {
      questions.push({
        id: 'result_destination',
        options: destinations,
        required: false,
      });
    }
  }

  return questions;
}

/**
 * Map output forms and S-class to the concrete persistence targets.
 */
export function deriveResultDestinations(
  outputForms: OutputForm[],
  sClass: DecisionClass,
): ResultDestination[] {
  const destinations = new Set<ResultDestination>();

  for (const form of outputForms) {
    switch (form) {
      case 'ranked_list':
      case 'scored_list':
        destinations.add('session_results');
        break;
      case 'tiered_list':
        destinations.add('session_results');
        if (sClass === 'S2_ITEM_POOL_SELECTION') {
          destinations.add('item_pool_update');
        }
        break;
      case 'two_by_two_map':
        destinations.add('session_results');
        break;
      case 'allocation_distribution':
        destinations.add('session_results');
        if (sClass === 'S2_ITEM_POOL_SELECTION') {
          destinations.add('item_pool_update');
        }
        break;
      case 'narrative_framing':
        destinations.add('narrative_artifact');
        if (sClass === 'S3_ATTRIBUTE_SELECTION') {
          destinations.add('criteria_artifact');
        }
        break;
    }
  }

  if (sClass === 'S3_ATTRIBUTE_SELECTION') {
    destinations.add('criteria_artifact');
  }

  return [...destinations];
}

// ============================================================================
// Main export: buildLaunchPlan
// ============================================================================

/**
 * Given a launch context, derive everything the SessionLauncher UI needs.
 *
 * This is a pure function — no side effects, no async, no Supabase calls.
 */
export function buildLaunchPlan(ctx: LaunchContext): LaunchPlan {
  const variants = getVariantsForTool(ctx.toolId);

  // Tool not found in registry
  if (variants.length === 0) {
    return {
      canLaunch: false,
      blockedReason: `Tool "${ctx.toolId}" is not in the method registry.`,
      sessionContextMode: 'app_linked',
      proposedSClass: 'S6_ITEM_PRIORITIZATION',
      variantFit: 'not_fit',
      selectedVariant: null,
      inheritedContext: {},
      questionsRequired: [],
      prerequisitesMet: [],
      prerequisitesBlocking: [],
      prerequisitesWarning: [],
      outputForms: [],
      resultDestinations: [],
    };
  }

  const contextMode = inferContextMode(ctx);
  const proposedSClass = inferProposedSClass(ctx, variants);
  const selectedVariant = selectBestVariant(ctx, variants, proposedSClass);
  const variantFit = selectedVariant?.sessionFit[proposedSClass] ?? 'not_fit';

  // Evaluate prerequisites
  const prereqs = selectedVariant?.prerequisites ?? [];
  const met: MethodPrerequisiteCode[] = [];
  const blocking: MethodPrerequisiteCode[] = [];
  const warning: MethodPrerequisiteCode[] = [];

  for (const prereq of prereqs) {
    const result = evaluatePrerequisite(prereq, ctx);
    if (result === 'met') met.push(prereq);
    else if (result === 'blocking') blocking.push(prereq);
    else warning.push(prereq);
  }

  // Can launch if: tool is valid, no hard blocking prerequisites
  // (for S2 class, fewer prereqs are needed)
  const effectiveBlocking = proposedSClass === 'S2_ITEM_POOL_SELECTION'
    ? blocking.filter((p) => p === 'PARTICIPATION_LOCKED')
    : blocking;

  const canLaunch = effectiveBlocking.length === 0;
  const blockedReason = !canLaunch
    ? `Missing prerequisites: ${effectiveBlocking.join(', ')}`
    : undefined;

  // Inherited context
  const inheritedContext: InheritedFields = {};
  if (ctx.cycleId) inheritedContext.cycleId = ctx.cycleId;
  if (ctx.trackId) {
    inheritedContext.trackId = ctx.trackId;
    inheritedContext.itemPoolSource = 'track_pool';
    if (ctx.isStandaloneTrack) inheritedContext.isStandaloneTrack = true;
  } else if (ctx.workspaceItemCount > 0) {
    inheritedContext.itemPoolSource = 'workspace_items';
  }
  inheritedContext.sessionContextMode = contextMode;
  if (selectedVariant && !hasMultipleModalities(variants)) {
    inheritedContext.modality = selectedVariant.modality as 'Qualitative' | 'Quantitative';
  }
  if (ctx.selectedMetaCategory) {
    inheritedContext.selectedMetaCategory = ctx.selectedMetaCategory;
  }

  // Questions
  const questionsRequired = buildQuestionsRequired(
    ctx, variants, proposedSClass, selectedVariant, contextMode,
  );

  const outputForms = selectedVariant?.outputForms ?? [];
  const resultDestinations = deriveResultDestinations(outputForms, proposedSClass);

  return {
    canLaunch,
    blockedReason,
    sessionContextMode: contextMode,
    proposedSClass,
    variantFit,
    selectedVariant,
    inheritedContext,
    questionsRequired,
    prerequisitesMet: met,
    prerequisitesBlocking: blocking,
    prerequisitesWarning: warning,
    outputForms,
    resultDestinations,
  };
}

function hasMultipleModalities(variants: MethodVariant[]): boolean {
  const modalities = new Set(variants.map((v) => v.modality));
  return modalities.size > 1;
}

// ============================================================================
// Prerequisite remediation links
// ============================================================================

/**
 * For each blocking prerequisite, returns a description and optional navigation link
 * so the launcher UI can show "go fix this" actions.
 */
export interface PrerequisiteRemediationInfo {
  code: MethodPrerequisiteCode;
  label: string;
  description: string;
  /** In-app path to resolve this prerequisite, if known */
  remedyPath?: string;
}

export function getPrerequisiteRemediation(
  prereq: MethodPrerequisiteCode,
  ctx: Partial<LaunchContext>,
): PrerequisiteRemediationInfo {
  const cycleSegment = ctx.cycleId ? `/prioritization/cycles/${ctx.cycleId}` : '/prioritization/cycles';
  const trackSegment = ctx.trackId
    ? `/prioritization/cycles/${ctx.cycleId ?? ''}/tracks/${ctx.trackId}`
    : cycleSegment;

  switch (prereq) {
    case 'METHOD_LOCKED':
      return {
        code: prereq,
        label: 'Method not selected',
        description: 'A prioritization method must be selected before the session can run.',
      };
    case 'PARTICIPATION_LOCKED':
      return {
        code: prereq,
        label: 'Participation not locked',
        description: 'Lock the participant list for this cycle/track before running.',
        remedyPath: `${trackSegment}?tab=participation`,
      };
    case 'POOL_LOCKED_FOR_S6':
      return {
        code: prereq,
        label: 'Item pool not locked',
        description: 'The item pool must be locked (S2 complete) before an S6 session can run.',
        remedyPath: `${trackSegment}?tab=pool`,
      };
    case 'CRITERIA_SET_LOCKED_FOR_S6':
      return {
        code: prereq,
        label: 'Criteria set not locked',
        description: 'Lock the criteria set (S3 complete) before scoring or ranking sessions.',
        remedyPath: `${trackSegment}?tab=criteria`,
      };
    case 'VALUES_COMPLETE_FOR_CRITERIA':
      return {
        code: prereq,
        label: 'Values incomplete',
        description: 'All items need values assigned for each criterion before this method can score them.',
        remedyPath: `${trackSegment}?tab=values`,
      };
    case 'LEVELING_PRESENT_WARN_ONLY':
      return {
        code: prereq,
        label: 'Altitude leveling present (warning)',
        description: 'Items span multiple altitudes. Results will be within-altitude only.',
      };
    case 'POOL_HOMOGENEITY_WARN_ONLY':
      return {
        code: prereq,
        label: 'Pool homogeneity warning',
        description: 'The item pool contains mixed types. Consider filtering before ranking.',
      };
    default:
      return {
        code: prereq,
        label: prereq,
        description: 'This prerequisite must be satisfied.',
      };
  }
}
